import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Package,
  MoreVertical,
  Eye,
  EyeOff,
  Star,
  StarOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGetDealsQuery, useDeleteDealMutation, useUpdateDealMutation } from "@/store";
import { TablePagination } from "@/components/admin";
import { toast } from "sonner";
import type { Deal, DealType } from "@/types/deals";

const DEFAULT_PAGE_SIZE = 20;

type StatusFilter = "all" | "active" | "inactive";
type FeaturedFilter = "all" | "featured" | "not_featured";
type DealTypeFilter = "all" | DealType;

export function AdminDealsScreen() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dealToDelete, setDealToDelete] = useState<Deal | null>(null);

  // Filters from URL params
  const statusFilter = (searchParams.get("statusFilter") as StatusFilter) || "all";
  const featuredFilter = (searchParams.get("featuredFilter") as FeaturedFilter) || "all";
  const dealTypeFilter = (searchParams.get("dealType") as DealTypeFilter) || "all";

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, featuredFilter, dealTypeFilter]);

  // Build query filters
  const queryFilters = {
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(statusFilter === "active" && { isActive: true }),
    ...(statusFilter === "inactive" && { isActive: false }),
    ...(featuredFilter === "featured" && { featured: true }),
    ...(featuredFilter === "not_featured" && { featured: false }),
    ...(dealTypeFilter && dealTypeFilter !== "all" && { dealType: dealTypeFilter }),
    page,
    limit,
  };

  const { data: dealsResponse, isLoading, error } = useGetDealsQuery(queryFilters);
  const [deleteDeal, { isLoading: isDeleting }] = useDeleteDealMutation();
  const [updateDeal, { isLoading: isUpdating }] = useUpdateDealMutation();

  const deals = dealsResponse?.data || [];
  const pagination = dealsResponse?.pagination;

  const handleStatusFilterChange = (value: StatusFilter) => {
    const params = new URLSearchParams(searchParams);
    if (value === "all") {
      params.delete("statusFilter");
    } else {
      params.set("statusFilter", value);
    }
    setSearchParams(params);
  };

  const handleFeaturedFilterChange = (value: FeaturedFilter) => {
    const params = new URLSearchParams(searchParams);
    if (value === "all") {
      params.delete("featuredFilter");
    } else {
      params.set("featuredFilter", value);
    }
    setSearchParams(params);
  };

  const handleDealTypeFilterChange = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value === "all") {
      params.delete("dealType");
    } else {
      params.set("dealType", value);
    }
    setSearchParams(params);
  };

  const handleToggleActive = async (deal: Deal) => {
    try {
      await updateDeal({
        id: deal.id,
        data: { isActive: !deal.isActive },
      }).unwrap();
      toast.success(`Deal ${deal.isActive ? "deactivated" : "activated"} successfully`);
    } catch (error) {
      console.error("Failed to update deal status:", error);
      toast.error("Failed to update deal status");
    }
  };

  const handleToggleFeatured = async (deal: Deal) => {
    try {
      await updateDeal({
        id: deal.id,
        data: { featured: !deal.featured },
      }).unwrap();
      toast.success(`Deal ${deal.featured ? "unfeatured" : "featured"} successfully`);
    } catch (error) {
      console.error("Failed to update deal featured status:", error);
      toast.error("Failed to update deal featured status");
    }
  };

  const handleDeleteDeal = async () => {
    if (!dealToDelete) return;

    try {
      await deleteDeal(dealToDelete.id).unwrap();
      toast.success("Deal deleted successfully");
      setDeleteDialogOpen(false);
      setDealToDelete(null);
    } catch (error) {
      console.error("Failed to delete deal:", error);
      toast.error("Failed to delete deal");
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
    }).format(price);
  };

  const getDealTypeLabel = (dealType: DealType) => {
    switch (dealType) {
      case "FIXED":
        return "Fixed";
      case "CONFIGURABLE":
        return "Configurable";
      case "COMPLEX":
        return "Complex";
      default:
        return dealType;
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-destructive">Failed to load deals</p>
          <p className="text-sm text-muted-foreground mt-2">
            Please try again later
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold">Deals Management</h1>
          <p className="text-muted-foreground">
            Manage your Cumbo deals and special offers
          </p>
        </div>
        <Button onClick={() => navigate("/admin/deals/new")} className="shrink-0">
          <Plus className="w-4 h-4 mr-2" />
          Create Deal
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search deals..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={featuredFilter} onValueChange={handleFeaturedFilterChange}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Featured" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Deals</SelectItem>
                <SelectItem value="featured">Featured</SelectItem>
                <SelectItem value="not_featured">Not Featured</SelectItem>
              </SelectContent>
            </Select>
            <Select value={dealTypeFilter} onValueChange={handleDealTypeFilterChange}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="FIXED">Fixed</SelectItem>
                <SelectItem value="CONFIGURABLE">Configurable</SelectItem>
                <SelectItem value="COMPLEX">Complex</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Deals Table */}
      <Card>
        <CardHeader>
          <CardTitle>Deals ({pagination?.total || 0})</CardTitle>
          <CardDescription>
            A list of all deals in your store
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : deals.length === 0 ? (
            <div className="text-center py-12">
              <Package className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-medium">No deals found</h3>
              <p className="text-muted-foreground mt-2">
                {debouncedSearch || statusFilter !== "all" || featuredFilter !== "all" || dealTypeFilter !== "all"
                  ? "Try adjusting your filters"
                  : "Get started by creating your first deal"}
              </p>
              {!debouncedSearch && statusFilter === "all" && featuredFilter === "all" && dealTypeFilter === "all" && (
                <Button
                  onClick={() => navigate("/admin/deals/new")}
                  className="mt-4"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Deal
                </Button>
              )}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Deal</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Featured</TableHead>
                    <TableHead>Components</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deals.map((deal) => (
                    <TableRow key={deal.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <img
                            src={deal.image}
                            alt={deal.name}
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                          <div>
                            <p className="font-medium">{deal.name}</p>
                            <p className="text-sm text-muted-foreground line-clamp-1">
                              {deal.description}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {getDealTypeLabel(deal.dealType)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{formatPrice(deal.price)}</div>
                        {deal.originalPrice && deal.originalPrice > deal.price && (
                          <div className="text-sm text-muted-foreground line-through">
                            {formatPrice(deal.originalPrice)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={deal.isActive ? "default" : "secondary"}>
                          {deal.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {deal.featured ? (
                          <Star className="w-4 h-4 text-yellow-500 fill-current" />
                        ) : (
                          <StarOff className="w-4 h-4 text-muted-foreground" />
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {deal.components.length} component{deal.components.length !== 1 ? "s" : ""}
                        </span>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuItem
                              onClick={() => navigate(`/admin/deals/${deal.id}/edit`)}
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleToggleActive(deal)}
                              disabled={isUpdating}
                            >
                              {deal.isActive ? (
                                <>
                                  <EyeOff className="w-4 h-4 mr-2" />
                                  Deactivate
                                </>
                              ) : (
                                <>
                                  <Eye className="w-4 h-4 mr-2" />
                                  Activate
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleToggleFeatured(deal)}
                              disabled={isUpdating}
                            >
                              {deal.featured ? (
                                <>
                                  <StarOff className="w-4 h-4 mr-2" />
                                  Unfeature
                                </>
                              ) : (
                                <>
                                  <Star className="w-4 h-4 mr-2" />
                                  Feature
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => {
                                setDealToDelete(deal);
                                setDeleteDialogOpen(true);
                              }}
                              className="text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {pagination && (
                <TablePagination
                  pagination={{
                    page,
                    limit,
                    total: pagination.total,
                    totalPages: pagination.totalPages,
                  }}
                  onPageChange={setPage}
                  onLimitChange={setLimit}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Deal</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{dealToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDeal}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}